/**
 * Retry schedule.
 *
 * MASTER_SPEC 3.5 requires exponential backoff with jitter; Design Bible 13.1
 * requires the retry state to be honest about it. Two schedules live here
 * because this surface has two genuinely different waits:
 *
 * - **Reads** retry automatically. A catalog page that failed can be fetched
 *   again with no consequence.
 * - **A payment is never auto-retried** (13.2, storefront row). Commands are
 *   attempted once and their failure is handed to the customer with a stated
 *   next action. The only thing that polls on a schedule is the *read* of
 *   authoritative checkout state, which is a GET and changes nothing.
 */

export const MAX_READ_ATTEMPTS = 5;

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;

/**
 * Full-jitter backoff: `random(0, min(cap, base * 2^(attempt - 1)))`, floored
 * at one second so the countdown is legible and does not flood a screen reader
 * (Design Bible 9.4 throttles announcements to one per 10 seconds).
 */
export function backoffDelayMs(attempt: number, random: () => number = Math.random): number {
  const exponent = Math.max(0, attempt - 1);
  const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** exponent);
  const jittered = Math.floor(random() * ceiling);
  return Math.max(BASE_DELAY_MS, jittered);
}

export function hasAttemptsLeft(attempt: number, maxAttempts = MAX_READ_ATTEMPTS): boolean {
  return attempt < maxAttempts;
}

export function secondsRemaining(nextAttemptAt: number, now: number): number {
  return Math.max(0, Math.ceil((nextAttemptAt - now) / 1_000));
}

/**
 * How often authoritative checkout state is re-read while core resolves a
 * provider outcome. Fixed rather than backed off: the customer is watching, the
 * request is a cheap read, and an accelerating gap would read as the page
 * having given up.
 */
export const AUTHORITATIVE_POLL_MS = 3_000;

/** A bound so a stuck order does not poll forever in a tab left open overnight. */
export const AUTHORITATIVE_POLL_LIMIT = 40;
